import { RECAPTCHA_SITE_KEY } from '@/config/constants';

// Action mapping for different endpoints
export const RECAPTCHA_ACTIONS = {
  // Authentication
  SIGNUP_VERIFICATION_EMAIL: 'signup_verification_email',
  SIGNUP: 'signup',
  LOGIN: 'login',
  PASSWORD_RESET_VERIFICATION_EMAIL: 'password_reset_verification_email',
  PASSWORD_RESET: 'password_reset',
  
  // Social Login
  GOOGLE_LOGIN: 'google_login',
  FACEBOOK_LOGIN: 'facebook_login',
  LINE_LOGIN: 'line_login',
  
  // User Info Update
  CHANGE_EMAIL_VERIFICATION: 'change_email_verification',
  CHANGE_EMAIL: 'change_email',
  CHANGE_PHONE_VERIFICATION: 'change_phone_verification',
  CHANGE_PHONE: 'change_phone',
  CHANGE_PASSWORD: 'change_password',
};

// Map API endpoints to reCAPTCHA actions
export const ENDPOINT_ACTION_MAP = {
  '/auth/signup/verification/email': RECAPTCHA_ACTIONS.SIGNUP_VERIFICATION_EMAIL,
  '/auth/signup': RECAPTCHA_ACTIONS.SIGNUP,
  '/auth/login': RECAPTCHA_ACTIONS.LOGIN,
  '/auth/password/verification/email': RECAPTCHA_ACTIONS.PASSWORD_RESET_VERIFICATION_EMAIL,
  '/auth/password': RECAPTCHA_ACTIONS.PASSWORD_RESET,
  '/auth/google/login': RECAPTCHA_ACTIONS.GOOGLE_LOGIN,
  '/auth/facebook/login': RECAPTCHA_ACTIONS.FACEBOOK_LOGIN,
  '/auth/line/login': RECAPTCHA_ACTIONS.LINE_LOGIN,
  '/users/me/email/verification': RECAPTCHA_ACTIONS.CHANGE_EMAIL_VERIFICATION,
  '/users/me/email': RECAPTCHA_ACTIONS.CHANGE_EMAIL,
  '/users/me/phone/verification': RECAPTCHA_ACTIONS.CHANGE_PHONE_VERIFICATION,
  '/users/me/phone': RECAPTCHA_ACTIONS.CHANGE_PHONE,
  '/users/me/password': RECAPTCHA_ACTIONS.CHANGE_PASSWORD,
};

// Track if reCAPTCHA is currently loaded
let isRecaptchaLoaded = false;
let recaptchaLoadPromise = null;

/**
 * Load reCAPTCHA Enterprise script with lazy loading
 * @returns {Promise<void>}
 */
export const loadRecaptchaScript = () => {
  // Return existing promise if already loading
  if (recaptchaLoadPromise) {
    return recaptchaLoadPromise;
  }

  // Return resolved promise if already loaded
  if (isRecaptchaLoaded && window.grecaptcha && window.grecaptcha.enterprise) {
    return Promise.resolve();
  }

  recaptchaLoadPromise = new Promise((resolve, reject) => {
    // Check if script element already exists
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      // Check if grecaptcha is already available
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        isRecaptchaLoaded = true;
        resolve();
        return;
      }

      // If script exists but grecaptcha isn't ready, wait for it
      const checkReady = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          isRecaptchaLoaded = true;
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };

      // Start checking immediately and also listen for load event
      checkReady();

      // Only add event listeners if they haven't been added yet
      if (!existingScript._recaptchaListenersAdded) {
        existingScript.addEventListener('load', checkReady);
        existingScript.addEventListener('error', reject);
        existingScript._recaptchaListenersAdded = true;
      }
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + RECAPTCHA_SITE_KEY;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Wait for grecaptcha to be ready
      const checkReady = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          isRecaptchaLoaded = true;
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };

    script.onerror = () => {
      recaptchaLoadPromise = null;
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    document.head.appendChild(script);
  });

  return recaptchaLoadPromise;
};

/**
 * Hide reCAPTCHA badge
 */
export const hideRecaptchaBadge = () => {
  const style = document.createElement('style');
  style.innerHTML = '.grecaptcha-badge { visibility: hidden !important; }';
  document.head.appendChild(style);
};

/**
 * Show reCAPTCHA badge
 */
export const showRecaptchaBadge = () => {
  const styles = document.querySelectorAll('style');
  styles.forEach(style => {
    if (style.innerHTML.includes('.grecaptcha-badge { visibility: hidden')) {
      style.remove();
    }
  });
};

/**
 * Get reCAPTCHA token for a specific action
 * @param {string} action - The action to get token for
 * @param {boolean} showBadge - Whether to show the badge during processing (default: false)
 * @returns {Promise<string>} - The reCAPTCHA token
 */
export const getRecaptchaToken = async (action, showBadge = false) => {
  try {
    // Only show badge if explicitly requested (e.g., for login/signup pages)
    if (showBadge) {
      showRecaptchaBadge();
    }

    // Ensure reCAPTCHA is loaded with timeout
    const loadTimeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('reCAPTCHA loading timeout')), 10000); // 10 second timeout
    });

    await Promise.race([loadRecaptchaScript(), loadTimeout]);

    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      throw new Error('reCAPTCHA Enterprise not available after loading');
    }

    // Execute reCAPTCHA and get token with timeout
    const executeTimeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('reCAPTCHA execution timeout')), 5000); // 5 second timeout
    });

    const tokenPromise = window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
      action: action
    });

    const token = await Promise.race([tokenPromise, executeTimeout]);

    if (!token) {
      throw new Error('Failed to get reCAPTCHA token');
    }

    // Hide badge after successful token generation if it was shown
    if (showBadge) {
      setTimeout(() => {
        hideRecaptchaBadge();
      }, 500); // Hide after 0.5 seconds
    }

    return token;
  } catch (error) {
    console.error('reCAPTCHA token generation failed:', error);
    // Hide badge on error too if it was shown
    if (showBadge) {
      setTimeout(() => {
        hideRecaptchaBadge();
      }, 500);
    }
    throw error;
  }
};

/**
 * Get reCAPTCHA action for an API endpoint
 * @param {string} url - The API endpoint URL
 * @returns {string|null} - The corresponding reCAPTCHA action or null if not protected
 */
export const getActionForEndpoint = (url) => {
  // Remove base URL and version prefix if present
  const cleanUrl = url.replace(/^.*\/v1/, '').split('?')[0];
  return ENDPOINT_ACTION_MAP[cleanUrl] || null;
};

/**
 * Check if an endpoint requires reCAPTCHA
 * @param {string} url - The API endpoint URL
 * @returns {boolean} - True if endpoint requires reCAPTCHA
 */
export const isProtectedEndpoint = (url) => {
  return getActionForEndpoint(url) !== null;
};

/**
 * Initialize reCAPTCHA settings on app startup
 * This hides the badge by default and preloads the script
 */
export const initializeRecaptcha = () => {
  // Hide reCAPTCHA badge by default
  hideRecaptchaBadge();

  // Preload reCAPTCHA script to avoid delays on first use
  loadRecaptchaScript()
    .then(() => {
      console.log('reCAPTCHA Enterprise preloaded successfully');
    })
    .catch((error) => {
      console.warn('reCAPTCHA Enterprise preload failed:', error);
      // Don't throw error - it will be retried when actually needed
    });
};

/**
 * Clean up reCAPTCHA when no longer needed
 * This can be called after user logs in to hide the badge permanently
 */
export const cleanupRecaptcha = () => {
  hideRecaptchaBadge();
  // Reset loading state for future use
  recaptchaLoadPromise = null;
};

/**
 * Reset reCAPTCHA loading state - useful for debugging or error recovery
 */
export const resetRecaptchaState = () => {
  isRecaptchaLoaded = false;
  recaptchaLoadPromise = null;
  console.log('reCAPTCHA state reset');
};

/**
 * Check if an error is likely due to reCAPTCHA verification failure
 * @param {Error} error - The error object from API response
 * @returns {boolean} - True if error is likely reCAPTCHA related
 */
export const isRecaptchaError = (error) => {
  return error.response?.status === 400 &&
         error.response?.data?.message?.toLowerCase().includes('recaptcha');
};

/**
 * Get user-friendly error message for reCAPTCHA failures
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export const getRecaptchaErrorMessage = (error) => {
  if (isRecaptchaError(error)) {
    return 'Security verification failed. Please try again.';
  }
  return error.response?.data?.message || 'An error occurred. Please try again.';
};

/**
 * Retry an API call with a fresh reCAPTCHA token
 * @param {Function} apiCall - The API function to retry
 * @param {string} action - The reCAPTCHA action
 * @param {Array} args - Arguments to pass to the API call
 * @returns {Promise} - The API response
 */
export const retryWithRecaptcha = async (apiCall, action, ...args) => {
  try {
    // First attempt
    return await apiCall(...args);
  } catch (error) {
    // If it's a potential reCAPTCHA error, retry once with a fresh token
    if (error.response?.status === 400) {
      console.log('Retrying API call with fresh reCAPTCHA token...');
      try {
        // Get a fresh token
        await getRecaptchaToken(action);
        // Retry the API call
        return await apiCall(...args);
      } catch (retryError) {
        console.error('Retry with fresh reCAPTCHA token failed:', retryError);
        throw retryError;
      }
    }
    throw error;
  }
};
